import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

// IBD Custom: Import the custom product update event.
import { IbdProductUpdateEvent, IbdVariantUpdateAbortedEvent } from '@theme/ibd-events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        // ! IBD Custom: SEO - Do not set the variant parameter in the URL
        // url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // ! IBD Custom: Fix combined listings impossible combinations stale option_values
    if (!source) {
      const currentProductUrl = this.dataset.productUrl?.split('?')[0];
      const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
      const switchesConnectedProduct =
        !!connectedProductUrl && !!currentProductUrl && connectedProductUrl !== currentProductUrl;

      // ! IBD Custom: Combined listings when the clicked option links to a different child product,
      // ! omit option_values. The child product URL already encodes the full selection; passing stale or partial
      // ! option_values from other fieldsets causes incorrect variant selection when product has multiple options.
      if (!switchesConnectedProduct && this.selectedOptionsValues.length) {
        params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      }
    } else if (source === 'product-card') {
      // ! IBD Custom: When a card swatch links to a combined-listing product URL, that URL already
      // ! encodes the full selection. The swatch's option value id is a parent-level value that does
      // ! not resolve on the child product, so passing it yields a null selected variant in the
      // ! Section Rendering response (empty prices, "$0" pre-order). Omit option_values in that case.
      if (!selectedOption.dataset.connectedProductUrl) {
        if (this.selectedOptionsValues.length) {
          params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
        } else {
          params.push(`option_values=${selectedOption.dataset.optionValueId}`);
        }
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      // ! IBD Custom: Make the response text async so updateMain can be async
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          // ! IBD Custom: Make the updateMain async so we can await
          // ! for the #injectMissingHeadStylesheets to load the missing stylesheets
          await this.updateMain(html);
          // ! IBD Custom: Re-morph the variant picker from pristine server HTML. A full <main> morph
          // ! can leave option radios with no checked input and stale data-current-checked attrs.
          newProduct = this.updateVariantPicker(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');

          // IBD Custom: Dispatch a custom variant update aborted event to notify the purchase type selector (we need to force a new fetch)
          this.dispatchEvent(new IbdVariantUpdateAbortedEvent({ requestUrl }));
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Capture server selection before morph — morph drains nodes from the source tree.
    const serverFieldsetSelections = this.#ibdCaptureServerFieldsetSelections(newVariantPickerSource);

    // ! IBD Custom: Morph with a clone so the parsed document stays intact for sync and events.
    // ! @see .ibd/references/ibd-known-issues-variant-update.md
    morph(this, newVariantPickerSource.cloneNode(true), {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#ibdSyncPickerRadioState(serverFieldsetSelections);
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * @param {Element} serverPicker
   * @returns {(string | undefined)[]}
   */
  #ibdCaptureServerFieldsetSelections(serverPicker) {
    return Array.from(serverPicker.querySelectorAll('fieldset.variant-option')).map((fieldset) => {
      const serverSelected = fieldset.querySelector(
        'input[type="radio"][data-current-checked="true"], input[type="radio"]:checked'
      );
      return serverSelected instanceof HTMLInputElement ? serverSelected.dataset.optionValueId : undefined;
    });
  }

  /**
   * IBD Custom: Align live radio checked state and animation attrs with the server-rendered picker.
   * Full-page morph can desync wood-type fieldsets (no :checked input, stale data-current-checked).
   *
   * @param {(string | undefined)[]} serverFieldsetSelections
   */
  #ibdSyncPickerRadioState(serverFieldsetSelections) {
    this.updatedCallback();

    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = /** @type {HTMLInputElement[]} */ (
        Array.from(fieldset.querySelectorAll('input[type="radio"]'))
      );
      this.#radios.push(radios);

      const selectedOptionValueId = serverFieldsetSelections[fieldsetIndex];

      if (selectedOptionValueId) {
        for (const radio of radios) {
          radio.checked = radio.dataset.optionValueId === selectedOptionValueId;
        }
      }

      for (const radio of radios) {
        radio.dataset.previousChecked = 'false';
        radio.dataset.currentChecked = radio.checked ? 'true' : 'false';

        if (radio.checked) {
          radio.setAttribute('checked', '');
        } else {
          radio.removeAttribute('checked');
        }
      }

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked);
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    // ! IBD Custom: Morph with a clone so the original newHtml stays intact for event consumers.
    // ! @see .ibd/references/ibd-known-issues-variant-update.md
    morph(element, newElement.cloneNode(true));
  }

  /**
   * Re-renders the entire main content.
   * ! IBD Custom: Make the updateMain async so we can await
   * ! for the #injectMissingHeadStylesheets to load the missing stylesheets
   * @param {Document} newHtml - The new HTML.
   * @returns {Promise<void>}
   */
  async updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // ! IBD Custom (CSS subsetting):
    // ! Shopify's automatic CSS subsetting (April 2026) means each page only loads the {% stylesheet %}
    // ! CSS for sections in its own render tree. When a combined-listings variant change morphs <main>
    // ! from a different product template that uses different sections (e.g. media-with-content), the
    // ! current page's styles.css does not contain those sections' rules. Mirror any missing
    // ! <link rel="stylesheet"> from the fetched page's <head> into the live <head> before the morph
    // ! so the new sections render styled.
    // ! @see https://shopify.dev/docs/storefronts/themes/best-practices/performance/stylesheet-subsetting
    // ! @see .ibd/references/ibd-known-issues-variant-update.md
    await this.#injectMissingHeadStylesheets(newHtml);

    // ! IBD Custom:
    // ! Morph with a clone so the original newHtml stays intact for event consumers (e.g. media-gallery
    // ! replaceWith). The morph algorithm moves nodes from newTree to oldTree, which would deplete the
    // ! parsed document when new has more children than old (e.g. variant with more media items).
    // ! @see .ibd/references/ibd-known-issues-variant-update.md
    morph(main, newMain.cloneNode(true));

    // IBD Custom: Dispatch a custom product update event after the main content is morphed.
    // Note: this should happen only if the variant picker is on the product page (see: loadsNewProduct)
    this.dispatchEvent(new IbdProductUpdateEvent({ html: newHtml, productId: this.dataset.productId ?? '' }));
  }

  /**
   * IBD Custom: Mirrors any missing stylesheet <link>s from the fetched page's <head> into the live
   * <head>, and waits for them to load. Handles `rel="stylesheet"`, combined `rel="preload stylesheet"`,
   * and `rel="preload" as="style"` patterns. Deduplicates by `href` so existing links are not re-added.
   *
   * Hot-path performance:
   * - Bails before scanning the live `<head>` when the fetched page has no stylesheet links at all
   *   (e.g. error/redirect responses).
   * - Builds the `existingHrefs` dedupe Set lazily, only after the first candidate `href` is seen
   *   in the fetched page's `<head>`.
   * - Allocates the `loadPromises` array lazily, only when at least one new render-applying
   *   stylesheet is actually being injected. The common case (variant change within the same product
   *   template = every new href already present) returns a pre-resolved promise with zero array
   *   allocations.
   * - Uses `relList.contains('stylesheet')` instead of splitting the `rel` attribute, avoiding a
   *   string + array allocation per link.
   * - Shallow-clones (`cloneNode(false)`) since `<link>` is a void element.
   *
   * @param {Document} newHtml - The full page document fetched from the new product URL.
   * @returns {Promise<void>}
   */
  #injectMissingHeadStylesheets(newHtml) {
    const stylesheetSelector = 'link[rel~="stylesheet"], link[rel="preload"][as="style"]';

    /** @type {NodeListOf<HTMLLinkElement>} */
    const newLinks = newHtml.head.querySelectorAll(stylesheetSelector);
    if (newLinks.length === 0) {
      return Promise.resolve();
    }

    /** @type {Set<string> | null} */
    let existingHrefs = null;

    /** @type {Promise<void>[] | null} */
    let loadPromises = null;

    for (const newLink of newLinks) {
      const href = newLink.getAttribute('href');
      if (!href) continue;

      if (existingHrefs === null) {
        existingHrefs = new Set();
        for (const existingLink of document.head.querySelectorAll(stylesheetSelector)) {
          const existingHref = existingLink.getAttribute('href');
          if (existingHref) existingHrefs.add(existingHref);
        }
      }

      if (existingHrefs.has(href)) continue;
      existingHrefs.add(href);

      const cloned = /** @type {HTMLLinkElement} */ (newLink.cloneNode(false));

      // Only block on links that actually apply styles to the document. Pure preloads load in the
      // background and don't trigger the morph FOUC we are guarding against. `relList.contains` is a
      // DOMTokenList lookup — constant time and allocation-free.
      if (newLink.relList.contains('stylesheet')) {
        (loadPromises ??= []).push(
          new Promise((resolve) => {
            const done = () => resolve();
            cloned.addEventListener('load', done, { once: true });
            cloned.addEventListener('error', done, { once: true });
          })
        );
      }

      document.head.appendChild(cloned);
    }

    return loadPromises === null
      ? Promise.resolve()
      : Promise.all(loadPromises).then(() => undefined);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
